import React from 'react';
import { Calendar, Sun, Cloud, Snowflake, Leaf, AlertCircle } from 'lucide-react';

const SeasonalCorrelations = ({ seasonal }) => {
  const getSeasonIcon = (season) => {
    switch (season) {
      case 'Winter':
        return <Snowflake className="w-6 h-6 text-blue-600" />;
      case 'Spring':
        return <Leaf className="w-6 h-6 text-green-600" />;
      case 'Summer':
        return <Sun className="w-6 h-6 text-yellow-600" />;
      case 'Fall':
        return <Cloud className="w-6 h-6 text-orange-600" />;
      default:
        return <Calendar className="w-6 h-6 text-gray-600" />;
    }
  };

  const getSeasonColor = (season) => {
    switch (season) {
      case 'Winter':
        return 'bg-blue-50 border-blue-200';
      case 'Spring':
        return 'bg-green-50 border-green-200';
      case 'Summer':
        return 'bg-yellow-50 border-yellow-200';
      case 'Fall':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const seasons = Object.entries(seasonal || {});
  const maxCount = Math.max(...seasons?.map(([_, data]) => data?.count), 1);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-teal-100 rounded-lg">
          <Calendar className="w-6 h-6 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Seasonal Patterns</h2>
          <p className="text-sm text-gray-600">Discover how seasons affect your symptoms</p>
        </div>
      </div>
      {seasons?.length > 0 ? (
        <>
          {/* Season Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {seasons?.map(([season, data]) => (
              <div key={season} className={`border rounded-lg p-5 ${getSeasonColor(season)}`}>
                <div className="flex items-center gap-3 mb-4">
                  {getSeasonIcon(season)}
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{season}</h3>
                    <p className="text-sm text-gray-600">{data?.count} symptom entries</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Average Severity</span>
                      <span className="font-medium text-gray-900">{data?.avgSeverity?.toFixed(1)}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          data?.avgSeverity >= 7
                            ? 'bg-red-500'
                            : data?.avgSeverity >= 4
                            ? 'bg-yellow-500' :'bg-green-500'
                        }`}
                        style={{ width: `${(data?.avgSeverity / 10) * 100}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Frequency</span>
                      <span className="font-medium text-gray-900">
                        {((data?.count / maxCount) * 100)?.toFixed(0)}% of total
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-600 h-2 rounded-full"
                        style={{ width: `${(data?.count / maxCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Top Symptoms */}
                {Object.keys(data?.symptoms || {})?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Common Symptoms</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(data?.symptoms)
                        ?.sort((a, b) => b?.[1] - a?.[1])
                        ?.slice(0, 3)
                        ?.map(([symptom, count]) => (
                          <span
                            key={symptom}
                            className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700"
                          >
                            {symptom} ({count})
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Comparison Chart */}
          <div className="border border-gray-200 rounded-lg p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Seasonal Comparison</h3>
            <div className="space-y-3">
              {seasons
                ?.sort((a, b) => b?.[1]?.avgSeverity - a?.[1]?.avgSeverity)
                ?.map(([season, data]) => (
                  <div key={season} className="flex items-center gap-3">
                    <div className="w-20 text-sm font-medium text-gray-700">{season}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                          <div
                            className={`h-6 rounded-full flex items-center justify-end pr-2 text-xs font-medium text-white ${
                              data?.avgSeverity >= 7
                                ? 'bg-red-500'
                                : data?.avgSeverity >= 4
                                ? 'bg-yellow-500' :'bg-green-500'
                            }`}
                            style={{ width: `${(data?.avgSeverity / 10) * 100}%` }}
                          >
                            {data?.avgSeverity?.toFixed(1)}
                          </div>
                        </div>
                        <span className="text-sm text-gray-600 w-16">{data?.count} entries</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Insights */}
          <div className="mt-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Sun className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-teal-900">
                <strong>Seasonal Insight:</strong>{' '}
                {seasons?.sort((a, b) => b?.[1]?.avgSeverity - a?.[1]?.avgSeverity)?.[0]?.[0]} shows the highest
                average severity ({seasons?.sort((a, b) => b?.[1]?.avgSeverity - a?.[1]?.avgSeverity)?.[0]?.[1]?.avgSeverity?.toFixed(1)}/10).
                Consider preparing preventive measures before this season.
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Not enough data for seasonal analysis</p>
          <p className="text-sm text-gray-500 mt-1">Track symptoms across multiple seasons to see patterns</p>
        </div>
      )}
    </div>
  );
};

export default SeasonalCorrelations;